/**
 * ReportPreviewTable — renders the live preview table for the report builder.
 * Shows headers, data rows, optional totals row from ReportPreviewData.
 * Handles loading, empty, and null-value states.
 */

import { useState } from 'react';

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import type { ReportPreviewData, ReportPreviewMeta } from '@/types/dashboard';

// ============================================================================
// TYPES
// ============================================================================

export interface ReportPreviewTableProps {
  data: ReportPreviewData | undefined;
  meta?: ReportPreviewMeta; // Phase 37 Plan 05 (D-11/D-13/D-14): cap + warning metadata
  isLoading: boolean;
  hasMinimumConfig: boolean;
  reportName: string;
  onSaveThenExport?: () => void; // Phase 37.1 D-C2: banner CTA handler
}

// ============================================================================
// HELPERS
// ============================================================================

function formatCell(value: string | number | null): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') {
    // Rates (small decimals < 100 with fractional part) get 2 decimal places
    if (!Number.isInteger(value) && Math.abs(value) < 1000) {
      return value.toFixed(2);
    }
    // Large integers get locale formatting
    if (Number.isInteger(value)) {
      return value.toLocaleString();
    }
    return value.toFixed(2);
  }
  return String(value);
}

// ============================================================================
// SKELETON TABLE
// ============================================================================

function SkeletonTable() {
  return (
    <div className="rounded-[14px] bg-surface shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-border-subtle">
        <Skeleton className="h-4 w-48" />
      </div>
      <div className="p-4 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-4 w-1/6" />
            <Skeleton className="h-4 w-1/6" />
            <Skeleton className="h-4 w-1/6" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ReportPreviewTable({
  data,
  meta,
  isLoading,
  hasMinimumConfig,
  reportName,
  onSaveThenExport,
}: ReportPreviewTableProps) {
  // Phase 37 Plan 05 (D-14): "Show all N rows" client-side expander.
  // The server returns up to PREVIEW_CAP rows (200). This local-state flag
  // flips between a client-side CLIENT_SLICE and all server-returned rows —
  // no second server call; data is already in memory.
  const [showAll, setShowAll] = useState(false);
  const CLIENT_SLICE = 200;
  const activityNotice = meta?.activityNotice;
  const hasNoMetaDailyActivity =
    activityNotice?.kind === 'noMetaDailyActivity';
  const activityNoticeTitle =
    activityNotice?.title || 'No Meta activity in this date range';

  // Empty state: no config yet
  if (!hasMinimumConfig) {
    return (
      <div className="rounded-[14px] bg-surface shadow-sm px-4 py-8 text-center">
        <div className="text-muted text-sm mb-1">No preview yet</div>
        <div className="text-faint text-xs">
          Add at least one row dimension and one metric to see a preview.
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return <SkeletonTable />;
  }

  // No data returned or malformed response (defensive guard against shape mismatch)
  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) {
    if (hasNoMetaDailyActivity) {
      return (
        <div
          data-testid="report-preview-activity-empty"
          className="rounded-[14px] bg-surface shadow-sm px-4 py-8 text-center"
        >
          <div className="text-primary text-sm font-semibold mb-1">
            {activityNoticeTitle}
          </div>
          <div className="text-secondary text-xs">
            {activityNotice.message}
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-[14px] bg-surface shadow-sm px-4 py-8 text-center">
        <div className="text-muted text-sm mb-1">No matching data</div>
        <div className="text-faint text-xs">
          The selected dimensions and metrics returned no results. Try different combinations or check that analytics data has been synced.
        </div>
      </div>
    );
  }

  // Phase 37 Plan 05 (D-13 frontend) + Phase 37.1 D-C2: soft warning banner.
  // Shows when meta.warning present OR whenever cap is applied (so the
  // Download-full-CSV CTA is discoverable even below the 5K soft-warning
  // threshold). Rendered ABOVE the header so users see it before scanning
  // columns. REUSES the existing report-preview-warning testid — do NOT split
  // into a second banner (Playwright selectors depend on this exact testid).
  const showWarningBanner = Boolean(meta?.warning) || Boolean(meta?.capApplied);
  const warningBanner = showWarningBanner ? (
    <div
      role="status"
      data-testid="report-preview-warning"
      className="px-4 py-2 bg-warning-soft border-b border-[#F59E0B]/40 text-xs text-warning flex items-center justify-between gap-2"
    >
      <span>
        {meta?.warning ??
          `Preview capped to ${data.rows.length.toLocaleString()} rows.`}
      </span>
      {meta?.capApplied && onSaveThenExport ? (
        <button
          type="button"
          onClick={onSaveThenExport}
          className="text-brand hover:text-brand font-medium whitespace-nowrap"
        >
          Download full CSV
        </button>
      ) : null}
    </div>
  ) : null;

  const activityNoticeBanner = hasNoMetaDailyActivity ? (
    <div
      role="status"
      data-testid="report-preview-activity-notice"
      className="px-4 py-2 bg-brand-soft border-b border-brand/20 text-xs text-brand"
    >
      {activityNotice.message}
    </div>
  ) : null;

  // Phase 37 Plan 05 (D-11/D-14 frontend): "Showing X of N rows" cap footer.
  // Rendered BELOW the table (inside the rounded outer) when server capped, or
  // when the client-side slice hides additional rows the server did return.
  const serverRowCount = data.rows.length;
  const displayRowCount = showAll ? serverRowCount : Math.min(serverRowCount, CLIENT_SLICE);
  const hasMoreInServer = Boolean(meta?.capApplied);
  const hasMoreInClient = serverRowCount > CLIENT_SLICE;
  const capFooter = hasMoreInServer || hasMoreInClient ? (
    <div className="flex items-center justify-between gap-2 px-4 py-2 text-xs text-muted border-t border-border-subtle bg-canvas">
      <span>
        Showing {displayRowCount.toLocaleString()} of {(meta?.totalRowCount ?? serverRowCount).toLocaleString()} rows
        {hasMoreInServer ? '. Narrow the date range or remove a dimension to see rows beyond the server cap.' : '.'}
      </span>
      {hasMoreInClient ? (
        <button
          type="button"
          data-testid="report-preview-show-all"
          onClick={() => setShowAll((v) => !v)}
          className="text-brand hover:text-brand font-medium"
        >
          {showAll ? 'Show less' : `Show all ${serverRowCount.toLocaleString()}`}
        </button>
      ) : null}
    </div>
  ) : null;

  // Slice displayed rows per current showAll flag (D-14 expander).
  const displayedRows = showAll ? data.rows : data.rows.slice(0, CLIENT_SLICE);

  return (
    <div className="rounded-[14px] bg-surface shadow-sm overflow-hidden">
      {activityNoticeBanner}
      {warningBanner}
      {/* Header with export */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div>
          <div className="text-sm font-semibold text-primary">Preview</div>
          <div className="text-[10px] text-muted mt-0.5">
            {displayRowCount} row{displayRowCount !== 1 ? 's' : ''}
          </div>
        </div>
        <CsvExport
          headers={data.headers}
          rows={displayedRows}
          filename={`${reportName || 'report'}-preview`}
          label={
            meta?.capApplied
              ? `Export visible (${displayRowCount.toLocaleString()} of ${(meta?.totalRowCount ?? serverRowCount).toLocaleString()} rows, capped)`
              : `Export visible (${displayRowCount.toLocaleString()} rows)`
          }
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border-subtle bg-canvas">
              {data.headers.map((header, idx) => (
                <th
                  key={header}
                  className={cn(
                    'px-4 py-2.5 text-muted font-medium whitespace-nowrap',
                    idx === 0 ? 'text-left' : 'text-right'
                  )}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={cn(
                  'border-b border-border-subtle',
                  rowIdx % 2 === 1 && 'bg-canvas'
                )}
              >
                {row.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    className={cn(
                      'px-4 py-2.5 whitespace-nowrap',
                      cellIdx === 0
                        ? 'font-medium text-primary text-left'
                        : 'text-right text-secondary'
                    )}
                  >
                    {formatCell(cell)}
                  </td>
                ))}
              </tr>
            ))}

            {/* Totals row */}
            {data.totals && (
              <tr className="border-t-2 border-border-subtle bg-canvas font-semibold">
                {data.totals.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    className={cn(
                      'px-4 py-2.5 whitespace-nowrap',
                      cellIdx === 0
                        ? 'text-primary text-left'
                        : 'text-right text-primary'
                    )}
                  >
                    {formatCell(cell)}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {capFooter}
    </div>
  );
}
